import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SupplierResponseTable } from "../InitialEvaluationStep/SupplierResponseTable";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { finalEvaluationSteps } from "guides/features/Evaluation";
import { SubmitEvaluationConfirmDialog } from "../../SubmitEvaluationConfirmDialog";
import { FinalSubmitConfirmation } from "./FinalSubmitConfirmation";
import { useInitialEvaluationContext } from "../../../hooks/InitialEvaluationContext";
import { useFinalSupplierScoring } from "../../../hooks/useFinalSupplierScoring";
import { useSupplierClarifications } from "../../../hooks/useSupplierClarifications";
import { useSupplierRevisions } from "../../../hooks/useSupplierRevisions";
import { submitFinalScores, getFinalScores } from "../../../api/final-scoring-api";
import { FinalScoringModal } from "./FinalScoringModal";
import type { SupplierScorePreview, CriterionScorePreview } from "../../../types/scoring.type";

const FINAL_INSTRUCTIONS: React.ReactNode[] = [
	"Review any clarification responses and supplier revisions before entering your final scores.",
	"Re-score each supplier against the evaluation criteria, taking into account any new information received.",
	"Your final scores will replace your initial scores and be used for the final evaluation report.",
	<>Once submitted, final scores are <strong>permanent</strong> and cannot be modified.</>,
];

interface FinalScoringContentProps {
	evalId: number;
	readOnlyLabel?: string;
	rfxId: number;
	onSubmitted?: () => void;
}

export const useFinalScoringState = (rfxId: number, evalId: number) => {
	const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
	const [showSubmitDialog, setShowSubmitDialog] = useState(false);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [justSubmitted, setJustSubmitted] = useState(false);
	const queryClient = useQueryClient();

	const {
		suppliers,
		isLoading: isSuppliersLoading,
		error,
		readOnly,
		memberFinalStatus,
		memberFinalSubmittedAt,
		refreshSupplier,
	} = useInitialEvaluationContext();

	const alreadySubmitted = memberFinalStatus === "submitted";
	const isSubmitted = justSubmitted || alreadySubmitted;

	const allFinalSuppliersScored =
		suppliers.length > 0 && suppliers.every((s) => s.finalStatus === "done");

	const scoring = useFinalSupplierScoring(rfxId, evalId, selectedInviteId ?? 0);
	const { data: clarifications } = useSupplierClarifications(rfxId, evalId, selectedInviteId ?? 0);
	const { data: revisionFiles } = useSupplierRevisions(rfxId, evalId, selectedInviteId ?? 0);

	useEffect(() => {
		if (selectedInviteId) {
			scoring.loadFinalScores(selectedInviteId);
		}
	}, [selectedInviteId]);

	const handleSelectSupplier = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleScoringComplete = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["final-scores"] });
	}, [queryClient]);

	const handleCloseModal = useCallback(() => {
		const closingInviteId = selectedInviteId;
		setSelectedInviteId(null);
		if (closingInviteId) {
			refreshSupplier(closingInviteId);
		}
	}, [selectedInviteId, refreshSupplier]);

	const handleSupplierChange = useCallback((inviteId: number) => {
		const previousInviteId = selectedInviteId;
		setSelectedInviteId(inviteId);
		if (previousInviteId) {
			refreshSupplier(previousInviteId);
		}
	}, [selectedInviteId, refreshSupplier]);

	const handleSubmit = useCallback(async () => {
		setIsSubmitting(true);
		try {
			await submitFinalScores(rfxId, evalId);
			queryClient.invalidateQueries({ queryKey: ["final-scores"] });
			queryClient.invalidateQueries({ queryKey: ["final-progress"] });
			setShowSubmitDialog(false);
			setConfirmChecked(false);
			refreshSupplier(0);
			setJustSubmitted(true);
		} finally {
			setIsSubmitting(false);
		}
	}, [rfxId, evalId, queryClient, refreshSupplier]);

	const supplierPreviews: SupplierScorePreview[] = suppliers.map((s) => ({
		inviteId: s.inviteId,
		supplierName: s.supplierName,
	}));

	const [criteriaPreviews, setCriteriaPreviews] = useState<CriterionScorePreview[]>([]);

	useEffect(() => {
		if (!showSubmitDialog || suppliers.length === 0) return;
		let cancelled = false;
		const load = async () => {
			const results = await Promise.allSettled(
				suppliers.map((s) => getFinalScores(rfxId, evalId, s.inviteId))
			);
			if (cancelled) return;
			const criteriaMap = new Map<number, CriterionScorePreview>();
			results.forEach((r) => {
				if (r.status !== "fulfilled") return;
				const detail = r.value;
				detail.criteria.forEach((c) => {
					const existing = criteriaMap.get(c.rfxCriteriaId) ?? {
						rfxCriteriaId: c.rfxCriteriaId,
						criterionName: c.criterionName,
						criterionWeight: c.criterionWeight,
						scores: {},
					};
					existing.scores[detail.inviteId] = {
						score: c.finalScore,
						initialScore: c.initialScore,
					};
					criteriaMap.set(c.rfxCriteriaId, existing);
				});
			});
			setCriteriaPreviews(Array.from(criteriaMap.values()));
		};
		load();
		return () => { cancelled = true; };
	}, [showSubmitDialog, rfxId, evalId, suppliers]);

	return {
		suppliers,
		isSuppliersLoading,
		error,
		readOnly,
		isSubmitted,
		memberFinalSubmittedAt,
		allFinalSuppliersScored,
		selectedInviteId,
		scoring,
		clarifications: clarifications ?? [],
		revisionFiles: revisionFiles ?? [],
		showSubmitDialog,
		setShowSubmitDialog,
		confirmChecked,
		setConfirmChecked,
		isSubmitting,
		supplierPreviews,
		criteriaPreviews,
		handleSelectSupplier,
		handleScoringComplete,
		handleCloseModal,
		handleSupplierChange,
		handleSubmit,
	};
};

export const FinalScoringContent: React.FC<FinalScoringContentProps> = ({
	evalId,
	rfxId,
	readOnlyLabel,
	onSubmitted,
}) => {
	const state = useFinalScoringState(rfxId, evalId);

	useEffect(() => {
		if (state.isSubmitted) {
			onSubmitted?.();
		}
	}, [state.isSubmitted, onSubmitted]);

	if (state.isSubmitted) {
		return (
			<FinalSubmitConfirmation
				rfxId={rfxId}
				evalId={evalId}
				suppliers={state.suppliers}
				submittedAt={state.memberFinalSubmittedAt}
			/>
		);
	}

	return (
		<div className="space-y-4">
			<InstructionsInfoBox title="Final Evaluation Scoring" instructions={FINAL_INSTRUCTIONS} guideSteps={finalEvaluationSteps} data-testid="final-instructions-info-box" />

			{readOnlyLabel && (
				<p className="text-sm text-gray-500">{readOnlyLabel}</p>
			)}

			{state.error && (
				<p className="text-sm text-red-600" data-testid="final-evaluation-error">
					{state.error}
				</p>
			)}

			<SupplierResponseTable
				suppliers={state.suppliers}
				isLoading={state.isSuppliersLoading}
				rfxId={rfxId}
				onSelectSupplier={state.readOnly ? () => undefined : state.handleSelectSupplier}
				readOnly={state.readOnly}
				isFinalMode
			/>

			{!state.readOnly && state.allFinalSuppliersScored && (
				<div className="flex justify-end">
					<button
						type="button"
						onClick={() => state.setShowSubmitDialog(true)}
						className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
						data-testid="final-submit-btn"
					>
						Submit Final Evaluation
					</button>
				</div>
			)}

			{state.selectedInviteId !== null && (
				<FinalScoringModal
					isOpen={state.selectedInviteId !== null}
					rfxId={rfxId}
					selectedInviteId={state.selectedInviteId}
					suppliers={state.suppliers}
					scoringDetail={state.scoring.scoringDetail}
					clarifications={state.clarifications}
					revisionFiles={state.revisionFiles}
					isLoading={state.scoring.isLoading}
					readOnly={state.readOnly}
					onClose={state.handleCloseModal}
					onScoringComplete={state.handleScoringComplete}
					onSupplierChange={state.handleSupplierChange}
					onSaveCriteria={state.scoring.saveFinalScore}
				/>
			)}

			<SubmitEvaluationConfirmDialog
				isOpen={state.showSubmitDialog}
				mode="final"
				suppliers={state.supplierPreviews}
				criteria={state.criteriaPreviews}
				confirmChecked={state.confirmChecked}
				onConfirmCheckedChange={state.setConfirmChecked}
				isSubmitting={state.isSubmitting}
				onConfirm={state.handleSubmit}
				onCancel={() => {
					state.setShowSubmitDialog(false);
					state.setConfirmChecked(false);
				}}
			/>
		</div>
	);
};
